import { TryStyleClient } from '../client';
import type { ComboProduct, ComboSection, TryStyleConfig } from '../types';

// Re-export public types so consumers import from one place
export type { FirebaseConfig, ModalOpts } from './modal-types';
export { injectStyles } from './styles';

import type { Phase, State, ViewContext, FirebaseConfig, ModalOpts } from './modal-types';
import { injectStyles } from './styles';
import { PDK, MUTED } from './tokens';
import { longLogoHTML, longLogoHTMLDark, pwLogoHTML } from './logos';
import { el, esc, parseSizeRec, toCanonicalCategory, tryonTypeFromTags } from './helpers';
import { resolveTryonType, comboCtaLabel } from './combo-chooser';
import {
  morphIn,
  morphTo,
  morphSize,
  measureSize,
  fade,
  hideContent,
  showContent,
  prefersReducedMotion,
} from './morph';
import { renderLogin } from './views/login';
import { getStoredHeightIn, getStoredHeightCm } from './height-dialog';
import { storage, STORAGE_KEYS } from '../utils/storage';
import { renderUpload }     from './views/upload';
import { renderHeight }     from './views/height';
import { renderAvatars }    from './views/avatars';
import { renderAvatarPreview } from './views/avatar-preview';
import { renderProcessing } from './views/processing';
import { renderResult }     from './views/result';

export interface TryOnButtonConfig extends TryStyleConfig {
  container:       HTMLElement;
  productId:       string | string[];
  productName?:    string;
  productImage?:   string;
  firebaseConfig?: FirebaseConfig;
  /**
   * Optional override. If omitted, the SDK will fetch origin-scoped config after PKCE
   * and use its retailerId / firebaseConfig.
   */
  retailerId?:     string;
  /** @default "Try This On" */
  label?:          string;
  /**
   * Product garment category. Controls which size recommendation rows are shown.
   * - 'top' | 'shirt' | 'upper'  → shows Upper size only
   * - 'bottom' | 'pants' | 'lower' | 'jeans' | 'skirt' → shows Lower size only
   * - 'dress' | 'jumpsuit' | 'outfit' → shows both Upper and Lower
   * - omitted / other → shows whichever sizes are available
   */
  category?:       string;
  onResult?:       (resultUrl: string, sizeRec?: { upper?: string | null; lower?: string | null } | null) => void;
  /**
   * When true, show full TryStyle branding (wordmark + tagline in the modal
   * header, "Powered by" row under the button, branded login link).
   * Default: false (lean).
   */
  showBranding?:   boolean;
  /**
   * Shimmer sweep on the trigger button.
   * - false / undefined → off (default)
   * - true → on, with defaults (left-to-right, diagonal)
   * - an object → full control over enabled / direction / diagonal / durationMs
   */
  shimmer?:        ShimmerConfig;
  /**
   * Modal color mode.
   * - 'auto' (default) → follows the OS preference (prefers-color-scheme)
   * - 'light' / 'dark' → force that theme regardless of the OS
   * Brand and configurator colors are never overridden; only semantic neutrals flip.
   */
  colorScheme?:    'auto' | 'light' | 'dark';
}

/** Sweep direction for the button shimmer effect. */
export type ShimmerDirection = 'left-to-right' | 'right-to-left';
/** Shimmer config: a boolean toggle or an explicit options object. */
export type ShimmerConfig = boolean | {
  enabled?:     boolean;
  /** Sweep direction. Default: 'left-to-right'. */
  direction?:   ShimmerDirection;
  /** Sweep along a diagonal instead of horizontally. Default: true. */
  diagonal?:    boolean;
  /** Duration of one full sweep in ms. Default: 2600. */
  durationMs?:  number;
};

interface ResolvedShimmer { on: boolean; dir: ShimmerDirection; diagonal: boolean; durationMs: number }

function resolveShimmer(shimmer?: ShimmerConfig): ResolvedShimmer {
  if (typeof shimmer === 'boolean') return { on: shimmer, dir: 'left-to-right', diagonal: true, durationMs: 2600 };
  if (shimmer && typeof shimmer === 'object') {
    return {
      on: shimmer.enabled ?? true,
      dir: shimmer.direction ?? 'left-to-right',
      diagonal: shimmer.diagonal ?? true,
      durationMs: shimmer.durationMs ?? 2600,
    };
  }
  return { on: false, dir: 'left-to-right', diagonal: true, durationMs: 2600 };
}

const INIT_ERR_MSG = 'Contact support to enable TryStyle on your site';

// ── Firebase lazy-loader ──────────────────────────────────────────────────────
const CDN = 'https://www.gstatic.com/firebasejs/10.12.0';

function loadScript(src: string): Promise<void> {
  return new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement('script');
    s.src = src; s.onload = () => res(); s.onerror = () => rej(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

async function getFirebaseAuth(cfg: FirebaseConfig): Promise<any> {
  const w = window as any;
  if (!w.firebase?.auth) {
    await loadScript(`${CDN}/firebase-app-compat.js`);
    await loadScript(`${CDN}/firebase-auth-compat.js`);
  }
  if (!w.firebase.apps.length) {
    w.firebase.initializeApp({ apiKey: cfg.apiKey, authDomain: cfg.authDomain, projectId: cfg.projectId });
  }
  return w.firebase.auth();
}

// ── MAIN MODAL ────────────────────────────────────────────────────────────────
const MORPH_MS = 360;
const FADE_MS = 120;

export function buildModal(
  opts: ModalOpts,
  client: TryStyleClient,
  originRect?: DOMRect,
  initialPhase?: Phase,
): () => void {
  const overlay = el('div', 'ts-overlay');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  if (opts.colorScheme && opts.colorScheme !== 'auto') {
    overlay.dataset.tsTheme = opts.colorScheme;
  }
  const card    = el('div', 'ts-card');
  const content  = el('div');
  card.appendChild(content);
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  const storedHeight = Number(getStoredHeightIn()) || 0;

  const state: State = {
    phase: initialPhase ?? 'upload', file: null, preview: null,
    videoUrl: null,
    progress: 0, resultUrl: null, resultImages: null, resultGcsPath: null, tryonJobId: null, tryonEventId: null,
    error: null, avatars: [], selectedAvatarId: null,
    viewsPending: false, multiviewError: null, multiviewRevealed: false,
    sizeRec: null, processingLabel: 'Generating your look...',
    resolvedCategory: null,
    avatarId: null, avatarStatus: 'idle',
    tryonType: opts.tryonType ?? null,
    comboOpen: false, comboLoading: false, comboError: null,
    comboSections: [], selectedComboProduct: null, comboCategories: null,
    heightFeet: storedHeight > 0 ? Math.floor(storedHeight / 12) : 5,
    heightInch: storedHeight > 0 ? storedHeight % 12 : 7,
    heightMenuOpen: false,
    heightMenuPage: 'ft',
    heightResolve: null,
    heightNext: 'upload',
    loginBackPhase: 'upload',
  };

  // On open: if user already logged in (not guest), go straight to avatars
  const _existingUser = client.auth.getCurrentUser();
  if (_existingUser && !_existingUser.isGuest && client.auth.isAuthenticated()) {
    state.phase = 'loading';
    client.avatars.list(1, 20).then(({ avatars }) => {
      set({
        phase: 'avatars', avatars, error: null,
        selectedAvatarId: avatars.find(a => a.isPrimary)?.id ?? avatars[0]?.id ?? null,
      });
    }).catch(() => {
      set({ phase: 'upload', error: null });
    });
  }

  // ── Transition orchestrator ───────────────────────────────────────────────
  // View/preview changes run a sequenced transition: fade content out → the
  // container's real width/height morph to the new geometry (in-flow, content
  // invisible during the swap/measure → no flicker) → the new content fades in.
  // Combo expansion morphs the box and reveals the carousel while keeping the
  // buttons visible. Non-geometry updates (progress, avatar status) render
  // instantly.
  let renderedOnce = false;
  let transitionBusy = false;
  let queuedRender = false;
  let lastPhase: Phase | null = null;
  let lastFile: File | null = null;
  let lastPreview: string | null = null;
  let lastComboOpen = false;
  let lastComboLoading = false;
  let lastComboLen = 0;

  function updateLastState(): void {
    lastPhase = state.phase;
    lastFile = state.file;
    lastPreview = state.preview;
    lastComboOpen = state.comboOpen;
    lastComboLoading = state.comboLoading;
    lastComboLen = state.comboSections.length;
  }

  function transitionType(): 'view' | 'combo' | null {
    if (state.phase !== lastPhase) return 'view';
    // Upload preview appearing / changing is a view change (card grows to the image).
    if (state.file !== lastFile || state.preview !== lastPreview) return 'view';
    if (
      state.comboOpen !== lastComboOpen ||
      state.comboLoading !== lastComboLoading ||
      state.comboSections.length !== lastComboLen
    ) {
      return 'combo';
    }
    return null;
  }

  async function transitionFor(type: 'view' | 'combo'): Promise<void> {
    if (prefersReducedMotion()) {
      renderView();
      return;
    }
    const old = card.getBoundingClientRect();

    // Preview → processing scan handoff: the preview <img> is the same cached
    // source the user already saw, so keep it visible and let morphSize fold the
    // button rows away while the sweep starts over the identical image. No fade
    // out/in — the image never reloads and the card doesn't shrink.
    const toScan = type === 'view' && state.phase === 'processing' && !!state.preview;
    if (toScan) {
      // 1. Pin the box to the old size, swap the view (image shows instantly).
      card.style.overflow = 'hidden';
      card.style.width = `${old.width}px`;
      card.style.height = `${old.height}px`;
      renderView();
      // 2. Measure the new natural size, then morph the real box to it (buttons
      //    collapse from the bottom; image keeps its natural ratio).
      const natural = measureSize(card, old.width, old.height);
      await morphSize(card, old.width, old.height, natural.width, natural.height, MORPH_MS);
      showContent(content);
      return;
    }

    // 1. (view) fade the current content out.
    if (type === 'view') await fade(content, 0, FADE_MS);
    // 2. Pin the box to the old size, swap the view (content invisible → no flicker).
    card.style.overflow = 'hidden';
    card.style.width = `${old.width}px`;
    card.style.height = `${old.height}px`;
    renderView();
    // 3. Measure the new natural size synchronously, then morph the real box to it.
    const natural = measureSize(card, old.width, old.height);
    await morphSize(card, old.width, old.height, natural.width, natural.height, MORPH_MS);
    // 4. (view) fade the new content in; (combo) ensure content is visible.
    if (type === 'view') await fade(content, 1, FADE_MS);
    else showContent(content);
  }

  /**
   * Post-load reflow: when an image loads and the card's natural size differs
   * from its current box, smoothly adjust (e.g. result/preview images).
   */
  function reflow(): void {
    if (prefersReducedMotion() || transitionBusy) return;
    const rect = card.getBoundingClientRect();
    card.style.overflow = 'hidden';
    card.style.width = `${rect.width}px`;
    card.style.height = `${rect.height}px`;
    const natural = measureSize(card, rect.width, rect.height);
    if (Math.abs(rect.width - natural.width) > 1 || Math.abs(rect.height - natural.height) > 1) {
      void morphSize(card, rect.width, rect.height, natural.width, natural.height, MORPH_MS);
    } else {
      card.style.overflow = '';
      card.style.width = '';
      card.style.height = '';
    }
  }

  function scheduleRender(): void {
    if (!renderedOnce) {
      renderedOnce = true;
      renderView();
      updateLastState();
      return;
    }
    if (transitionBusy) {
      queuedRender = true;
      return;
    }
    const type = transitionType();
    if (!type) {
      renderView();
      return;
    }
    updateLastState();
    transitionBusy = true;
    void (async () => {
      let t: 'view' | 'combo' | null = type;
      do {
        queuedRender = false;
        if (t) {
          await transitionFor(t);
        } else {
          renderView();
        }
        t = transitionType();
        if (t) updateLastState();
      } while (queuedRender);
      transitionBusy = false;
    })();
  }

  function set(partial: Partial<State>): void {
    Object.assign(state, partial);
    scheduleRender();
  }

  // ── Avatar upload (silent background) ───────────────────────────────────────
  // Uploads the avatar the moment a photo is selected so "Try This On" doesn't
  // have to wait for it. Deduplicated via the in-flight promise; also reused by
  // the submit flow (they are not co-dependent, but the tryon call needs the id).
  //
  // ponytail: the cached avatarId (and in-flight upload) are tied to the exact
  // File that produced them. After "Try Again" + choosing a new photo, the old
  // id must NOT be reused — the avatar API must fire again for a fresh id, or
  // the try-on runs on the previous photo's avatar.
  let pendingAvatarUpload: Promise<string> | null = null;
  let pendingAvatarFile: File | null = null;
  let uploadedAvatarFile: File | null = null;

  function ensureAvatarUploaded(file: File): Promise<string> {
    if (state.avatarId && uploadedAvatarFile === file) return Promise.resolve(state.avatarId);
    if (pendingAvatarUpload && pendingAvatarFile === file) return pendingAvatarUpload;

    pendingAvatarFile = file;
    pendingAvatarUpload = (async (): Promise<string> => {
      set({ avatarStatus: 'uploading', avatarId: null, sizeRec: null });
      uploadedAvatarFile = null;
      try {
        if (!client.auth.isAuthenticated()) {
          await client.auth.getGuestToken();
        }
        let heightCm = getStoredHeightCm();
        if (!heightCm) {
          heightCm = await new Promise<number | null>((resolve) => {
            // Show the height phase inside the modal (same shared element), then
            // return to the current phase once confirmed.
            state.heightResolve = resolve;
            state.heightNext = state.phase;
            set({ phase: 'height' });
          });
          if (!heightCm) throw new Error('Height is required for size recommendation.');
        }
        const avatar = await client.avatars.upload(file, { heightCm, gender: 'female' });
        let sizeRec: State['sizeRec'] = null;
        try {
          // Use measurements from upload response (PBA runs synchronously during upload)
          const m = avatar.measurements ?? (await client.avatars.getMeasurements(avatar.id))?.measurements;
          sizeRec = parseSizeRec(m?.size_recommendation);
        } catch { /* measurements are optional */ }
        storage.set(STORAGE_KEYS.AVATAR_CACHE, JSON.stringify({ avatarId: avatar.id, sizeRec }));
        uploadedAvatarFile = file;
        set({ avatarId: avatar.id, avatarStatus: 'ready', sizeRec });
        return avatar.id;
      } catch (e) {
        set({ avatarStatus: 'error' });
        throw e;
      } finally {
        pendingAvatarUpload = null;
        pendingAvatarFile = null;
      }
    })();

    return pendingAvatarUpload;
  }

  // Photo selected through the file picker (upload view + avatars view's
  // "+ Upload New"). Sets the preview object URL and starts the silent avatar
  // upload; "Try This On" awaits the same in-flight request later.
  function selectFile(f: File): void {
    if (!f.type.startsWith('image/')) return;
    if (state.preview) URL.revokeObjectURL(state.preview);
    set({ file: f, preview: URL.createObjectURL(f) });
    void ensureAvatarUploaded(f);
  }

  // Source a same-origin preview URL for the avatar-scan sweep. Preferred path is
  // the API image proxy (CORS-safe) when a byte route exists; otherwise fall back
  // to the GCS signedUrl directly — a cross-origin <img> still loads and draws
  // fine (only pixel reads are CORS-restricted, and the sweep engine handles that
  // with a plain laser/tint tier). Returns null only when the avatar has no image.
  async function avatarPreviewUrl(avatarId: string): Promise<string | null> {
    try {
      const blob = await client.avatars.downloadImageBlob(avatarId);
      return URL.createObjectURL(blob);
    } catch (proxyErr) {
      try {
        const avatar =
          state.avatars.find((a) => a.id === avatarId) ?? (await client.avatars.get(avatarId));
        return avatar.signedUrl || null;
      } catch {
        return null;
      }
    }
  }

  let destroyed = false;
  // Run counter for the background multiview poll: each new try-on bumps it so a
  // stale poll from a previous attempt can never overwrite the current result.
  let tryonRunSeq = 0;

  function destroy(): void {
    if (destroyed) return;
    destroyed = true;
    if (state.preview) URL.revokeObjectURL(state.preview);
    if (originRect && !prefersReducedMotion()) {
      void (async () => {
        await fade(content, 0, FADE_MS);
        morphTo(card, originRect, MORPH_MS, () => overlay.remove());
      })();
    } else {
      overlay.remove();
    }
  }

  overlay.addEventListener('click', (e) => {
    if (e.target !== overlay || state.phase === 'processing') return;
    if (state.phase === 'login') {
      set({ phase: state.loginBackPhase, error: null });
    } else {
      destroy();
    }
  });

  // ── shared ────────────────────────────────────────────────────────────────
  function headerHTML(): string {
    // ponytail: lean by default — no header bar at all. Full wordmark + tagline
    // only when showBranding is opted in.
    if (!opts.showBranding) return '';
    const dark =
      opts.colorScheme === 'dark' ||
      (opts.colorScheme !== 'light' &&
        typeof window !== 'undefined' &&
        !!window.matchMedia?.('(prefers-color-scheme: dark)').matches);
    const wordmark = dark ? longLogoHTMLDark(32) : longLogoHTML(32);
    const brand = `<div>${wordmark}</div><p class="ts-mhdr-tagline">Experience the future of online shopping with AI technology</p>`;
    return `<div class="ts-mhdr">${brand}<div class="ts-divider"></div></div>`;
  }

  function openLogin(): void {
    set({ phase: 'login', loginBackPhase: 'upload', error: null });
  }

  async function handleLoginSuccess(): Promise<void> {
    // Show loading state immediately while fetching avatars
    renderAvatarLoading();
    try {
      const { avatars } = await client.avatars.list(1, 20);
      // Always transition to avatars phase (even if empty — show Upload New CTA)
      set({
        phase: 'avatars',
        avatars,
        error: null,
        selectedAvatarId: avatars.find(a => a.isPrimary)?.id ?? avatars[0]?.id ?? null,
      });
    } catch (e: any) {
      // Show error inside the (now visible) main modal
      set({ phase: state.loginBackPhase, error: e?.message ?? 'Failed to load avatars. Please try again.' });
    }
  }

  function renderAvatarLoading(): void {
    content.innerHTML = `
      ${headerHTML()}
      <div class="ts-mbody" style="text-align:center;padding:var(--ts-modal-pad)">
        <div class="ts-spinner" style="margin:0 auto 16px"></div>
        <p style="color:${MUTED};font-size:14px;margin:0">Loading your avatars…</p>
      </div>
    `;
  }

  // ── Shared try-on runner ──────────────────────────────────────────────────
  // All three submit paths funnel through here. With split delivery the front
  // result returns first (~13s): the modal moves to the result phase showing the
  // front image plus a minimal "Generating side and back view…" indicator, then
  // the side/back slide is appended in place when it lands. Without it the job
  // completes as before. Fires `opts.onResult` once — at the front result.

  // Preload result images while the scan keeps running, so the processing →
  // result transition has the image ready: the result view renders with it in
  // the browser cache → no blank collapse, the modal morphs to the real size and
  // the image fades in. Bounded by a timeout so a dead URL can't hang the flow.
  function preloadImages(urls: Array<string | null | undefined>, timeoutMs = 12000): Promise<void> {
    return Promise.all(
      urls.filter((u): u is string => !!u).map((url) => new Promise<void>((resolve) => {
        const img = new Image();
        const timer = window.setTimeout(done, timeoutMs);
        function done(): void { window.clearTimeout(timer); resolve(); }
        img.onload = done;
        img.onerror = done;
        img.src = url;
      })),
    ).then(() => undefined);
  }

  async function runTryOn(submitOpts: {
    avatarId: string;
    retailerId: string;
    productId: string | string[];
    category?: string | string[];
    productImage?: string;
    sizeRec?: State['sizeRec'];
    /** Fallback resolved category for the size card when the backend omits one. */
    resolvedCategoryFallback?: string;
  }): Promise<void> {
    // Fresh result: allow the one-time front→side→front reveal again.
    state.multiviewRevealed = false;
    const runToken = ++tryonRunSeq;
    const unsubProgress = client.on('tryon:progress', ({ progress: pct }) => {
      set({ progress: pct, processingLabel: 'Creating Look...' });
    });

    const resolveCategory = (backend: string | null | undefined): string | null =>
      backend ?? submitOpts.resolvedCategoryFallback ?? opts.category ?? null;

    try {
      const job = await client.tryon.submit(submitOpts);

      // ── Direct / split-delivery: front result available on submit ────────
      if ((job.status === 'success' || job.progress === 100) && job.result_image_url) {
        const frontImages = (job.result_image ?? [job.result_image_url]).filter(Boolean) as string[];
        const resolvedCat = resolveCategory(job.category);
        const front = job.result_image_url;

        // Keep the scan sweep running while the result image(s) load into cache,
        // so the result view renders with them ready (no blank/collapse morph).
        await preloadImages(frontImages);

        if (job.multiview_pending && job.multiview_status_endpoint) {
          set({
            phase: 'result', progress: 100,
            resultUrl: front, resultImages: frontImages,
            resultGcsPath: job.result_gcs_path ?? null,
            tryonJobId: job.job_id ?? null,
            tryonEventId: job.event_id ?? null,
            viewsPending: true, multiviewError: null,
            resolvedCategory: resolvedCat,
          });
          opts.onResult?.(front ?? '', submitOpts.sizeRec ?? state.sizeRec);
          // Background poll for the side/back view, then fill the slider in place.
          void client.tryon.pollMultiviewUntilDone(job.event_id)
            .then((mv) => {
              if (destroyed || runToken !== tryonRunSeq) return;
              if (mv.multiview_url) {
                set({ resultImages: [...frontImages, mv.multiview_url], viewsPending: false });
              } else {
                set({ viewsPending: false, multiviewError: mv.multiview_error ?? 'Side/back view unavailable.' });
              }
            })
            .catch(() => {
              if (destroyed || runToken !== tryonRunSeq) return;
              set({ viewsPending: false, multiviewError: 'Side/back view unavailable.' });
            });
        } else {
          set({
            phase: 'result', progress: 100,
            resultUrl: front, resultImages: frontImages,
            resultGcsPath: job.result_gcs_path ?? null,
            tryonJobId: job.job_id ?? null,
            tryonEventId: job.event_id ?? null,
    viewsPending: false, multiviewError: null, multiviewRevealed: false,
            resolvedCategory: resolvedCat,
          });
          opts.onResult?.(front ?? '', submitOpts.sizeRec ?? state.sizeRec);
        }
        unsubProgress();
        return;
      }

      // ── Job-based model: poll to completion ──────────────────────────────
      if (!job.job_id) {
        throw new Error(`Try-on submit did not return a job_id or an immediate result. event_id=${job.event_id}`);
      }
      const result = await client.tryon.pollUntilDone(job.job_id);
      const images = (result.result_image && result.result_image.length
        ? result.result_image
        : (result.result_image_url ? [result.result_image_url] : [])) as string[];
      const resolvedCat = resolveCategory(result.category);

      // Preload so the result view renders with the image ready (no blank).
      await preloadImages(images);

      if (result.multiview_pending && result.multiview_status_endpoint) {
        set({
          phase: 'result', progress: 100,
          resultUrl: result.result_image_url, resultImages: images,
          resultGcsPath: result.result_gcs_path ?? null,
          tryonJobId: result.job_id ?? null,
          tryonEventId: result.event_id ?? null,
          viewsPending: true, multiviewError: null,
          resolvedCategory: resolvedCat,
        });
        opts.onResult?.(result.result_image_url ?? '', submitOpts.sizeRec ?? state.sizeRec);
        void client.tryon.pollMultiviewUntilDone(result.event_id)
          .then((mv) => {
            if (destroyed || runToken !== tryonRunSeq) return;
            if (mv.multiview_url) set({ resultImages: [...images, mv.multiview_url], viewsPending: false });
            else set({ viewsPending: false, multiviewError: mv.multiview_error ?? 'Side/back view unavailable.' });
          })
          .catch(() => {
            if (destroyed || runToken !== tryonRunSeq) return;
            set({ viewsPending: false, multiviewError: 'Side/back view unavailable.' });
          });
      } else {
        set({
          phase: 'result', progress: 100,
          resultUrl: result.result_image_url, resultImages: images,
          resultGcsPath: result.result_gcs_path ?? null,
          tryonJobId: result.job_id ?? null,
          tryonEventId: result.event_id ?? null,
          viewsPending: false, multiviewError: null,
          resolvedCategory: resolvedCat,
        });
        opts.onResult?.(result.result_image_url ?? '', submitOpts.sizeRec ?? state.sizeRec);
      }
      unsubProgress();
    } catch (e: any) {
      unsubProgress();
      throw e;
    }
  }

  async function handleGuestOrAuthSubmit(): Promise<void> {
    if (!state.file) return;

    set({ phase: 'processing', progress: 0, error: null, processingLabel: 'Generating your look...', comboOpen: false, comboSections: [], selectedComboProduct: null, comboCategories: null });

    try {
      // Avatar may still be uploading in the background — await the same
      // in-flight request (or upload now if it never started).
      const avatarId = await ensureAvatarUploaded(state.file);

      let sizeRec: State['sizeRec'] = state.sizeRec ?? null;
      try {
        if (!sizeRec) {
          const m = (await client.avatars.get(avatarId))?.measurements ?? (await client.avatars.getMeasurements(avatarId))?.measurements;
          sizeRec = parseSizeRec(m?.size_recommendation);
        }
      } catch { /* no measurements yet */ }

      await runTryOn({
        avatarId, retailerId: opts.retailerId, productId: opts.productId,
        ...(opts.productImage !== undefined && { productImage: opts.productImage }),
        ...(opts.category !== undefined && { category: opts.category }),
        sizeRec,
      });
    } catch (e: any) {
      // Logged-in users came here from the avatars view — send them back there, not to the guest upload screen
      const isRealUser = client.auth.isAuthenticated() && !client.auth.getCurrentUser()?.isGuest;
      const errorPhase: Phase = isRealUser ? 'avatars' : 'upload';
      set({ phase: errorPhase, error: e?.message ?? 'Try-on failed. Please try again.' });
    }
  }

  async function handleAvatarSubmit(avatarId: string): Promise<void> {
    // Surface the avatar image on the processing screen (scan sweep) too.
    const preview = await avatarPreviewUrl(avatarId);
    set({ phase: 'processing', progress: 0, error: null, preview, processingLabel: 'Generating your look...', comboOpen: false, comboSections: [], selectedComboProduct: null, comboCategories: null });

    try {
      let sizeRec: State['sizeRec'] = null;
      try {
        const meas = await client.avatars.getMeasurements(avatarId);
        sizeRec = parseSizeRec(meas?.measurements?.size_recommendation);
      } catch { /* ok */ }

      await runTryOn({
        avatarId, retailerId: opts.retailerId, productId: opts.productId,
        ...(opts.productImage !== undefined && { productImage: opts.productImage }),
        ...(opts.category !== undefined && { category: opts.category }),
        sizeRec,
      });
    } catch (e: any) {
      const backPhase = client.auth.isAuthenticated() && !client.auth.getCurrentUser()?.isGuest ? 'avatars' : 'upload';
      set({ phase: backPhase as Phase, error: e?.message ?? 'Try-on failed.' });
    }
  }

  // ── COMBO TRY-ON ─────────────────────────────────────────────────────────
  function resolveModalTryonType(): string | null {
    return resolveTryonType(opts, state);
  }

  function selectComboProduct(p: ComboProduct): void {
    const next = state.selectedComboProduct?.id === p.id ? null : p;
    // Update state + DOM in place — no full re-render, so the row doesn't flash.
    state.selectedComboProduct = next;
    content.querySelectorAll<HTMLElement>('.ts-combo-item').forEach((tile) => {
      tile.classList.toggle('selected', tile.dataset.id === String(next?.id ?? ''));
    });
    const cta = content.querySelector<HTMLElement>('#ts-combo-cta');
    if (cta) cta.textContent = comboCtaLabel(state);
  }

  async function openCombo(): Promise<void> {
    const tryonType = resolveModalTryonType();
    if (!tryonType) {
      set({
        comboOpen: true, comboLoading: false, comboSections: [],
        comboError: 'Combo try-on is not available for this product.',
      });
      return;
    }
    set({ comboOpen: true, comboLoading: true, comboError: null, comboSections: [], selectedComboProduct: null });
    // Pin the card's current rendered width so the carousel can only grow the
    // height — the width must not change when the garments load.
    if (!card.style.width) {
      card.style.width = `${card.offsetWidth}px`;
    }
    try {
      // Ensure the avatar (and guest token for the guest flow) is available
      // before fetching — never let a combo tap break the primary flow.
      if (state.file) {
        try { await ensureAvatarUploaded(state.file); } catch { /* products fetch surfaces its own error */ }
      }
      const combos = await client.combo.getCombinations(opts.retailerId);
      const partnerCats = new Set<string>();
      for (const combo of combos) {
        // Backend validates exact category sets (by design) — a 2-garment
        // submit only passes when it exactly matches a configured 2-category
        // combo. Multi-category combos can't be satisfied by a 2-garment
        // submit, so only exact 2-element combos are offered.
        const cats = combo.categories
          .map((c) => toCanonicalCategory(c))
          .filter((c): c is string => !!c);
        if (cats.length !== 2) continue;
        const idx = cats.indexOf(tryonType);
        if (idx === -1) continue;
        partnerCats.add(idx === 0 ? cats[1] : cats[0]);
      }
      const sections: ComboSection[] = [];
      for (const cat of [...partnerCats]) {
        try {
          // Products are server-filtered to this canonical category, so pin
          // the submit category to the section's — guarantees a valid pair.
          const fetched = await client.combo.getProducts(opts.retailerId, cat, 10);
          // Lenient client-side verify: if the response carries tags, drop any
          // product whose tag-derived type doesn't match this section (protects
          // against a backend that still filters by category). If no tags are
          // present, trust the server filter.
          const hasTags = fetched.some((p) => Array.isArray(p.tags) && p.tags.length > 0);
          const products = (hasTags
            ? fetched.filter((p) => tryonTypeFromTags(p.tags) === cat)
            : fetched
          ).map((p): ComboProduct => ({ ...p, category: cat }));
          if (products.length > 0) sections.push({ category: cat, label: cat, products });
        } catch {
          /* skip a failing category — others still render */
        }
      }

      if (sections.length === 0) {
        set({ comboOpen: true, comboLoading: false, comboSections: [], comboError: 'No compatible garments found for this item.' });
        return;
      }
      set({ comboOpen: true, comboLoading: false, comboError: null, comboSections: sections });
    } catch (e: any) {
      set({
        comboOpen: true, comboLoading: false, comboSections: [],
        comboError: e?.message ?? 'Failed to load compatible garments. Please try again.',
      });
    }
  }

  async function handleComboSubmit(): Promise<void> {
    const avatarId = state.file ? await ensureAvatarUploaded(state.file) : state.selectedAvatarId;
    const selected = state.selectedComboProduct;
    const tryonType = resolveModalTryonType();
    if (!avatarId || !selected || !tryonType) return;

    // Avatar-invoked combo: surface the avatar (not a photo) on the scan sweep.
    const preview = state.file ? state.preview : await avatarPreviewUrl(avatarId);
    set({ phase: 'processing', progress: 0, error: null, preview, processingLabel: 'Generating your look...', comboOpen: false, comboSections: [], selectedComboProduct: null, comboCategories: null });

    try {
      let sizeRec: State['sizeRec'] = null;
      try {
        const meas = await client.avatars.getMeasurements(avatarId);
        sizeRec = parseSizeRec(meas?.measurements?.size_recommendation);
      } catch { /* ok */ }

      const pdpId = Array.isArray(opts.productId) ? String(opts.productId[0] ?? '') : String(opts.productId ?? '');
      const comboCategories = [tryonType, selected.category ?? ''];
      await runTryOn({
        avatarId, retailerId: opts.retailerId,
        productId: [pdpId, String(selected.id)],
        category: comboCategories,
        sizeRec,
        resolvedCategoryFallback: tryonType,
      });
    } catch (e: any) {
      const backPhase: Phase = client.auth.isAuthenticated() && !client.auth.getCurrentUser()?.isGuest ? 'avatars' : 'upload';
      set({ phase: backPhase, error: e?.message ?? 'Combo try-on failed. Please try again.', comboOpen: false });
    }
  }

  async function onComboCta(): Promise<void> {
    if (state.comboOpen) {
      if (state.selectedComboProduct) {
        await handleComboSubmit();
      } else {
        set({ comboOpen: false, comboError: null, comboSections: [] });
      }
    } else {
      await openCombo();
    }
  }

  function renderView(): void {
    // Keep the card a normal white card in every phase (the wait screen morphs
    // smoothly instead of the transparent-card "drop").
    card.style.background = '';
    card.style.boxShadow  = '';
    // Preview modal hugs the image at its natural ratio — same as the processing
    // scan — for both the guest upload preview and the logged-in avatar preview.
    card.classList.toggle('ts-card-preview', (state.phase === 'upload' && !!state.file) || state.phase === 'avatar-preview');
    card.classList.toggle('ts-card-upload', state.phase === 'upload');
    card.classList.toggle('ts-card-avatars', state.phase === 'avatars');
    card.classList.toggle('ts-card-combo', state.comboOpen);
    card.classList.toggle('ts-card-height', state.phase === 'height');
    card.classList.toggle('ts-card-glitch', state.phase === 'processing' && !!state.preview);
    card.classList.toggle('ts-card-login', state.phase === 'login');

    const ctx: ViewContext = {
      state, opts, content, client,
      headerHTML, openLogin, set,
      handleLoginSuccess,
      ensureAvatarUploaded, selectFile,
      handleGuestOrAuthSubmit, handleAvatarSubmit,
      selectComboProduct, onComboCta,
      reflow,
      renderSizeCard,
    };
    switch (state.phase) {
      case 'height':        renderHeight(ctx);        break;
      case 'upload':        renderUpload(ctx);         break;
      case 'login':         renderLogin(ctx);          break;
      case 'avatars':       renderAvatars(ctx);        break;
      case 'avatar-preview': renderAvatarPreview(ctx);  break;
      case 'processing':    renderProcessing(ctx);     break;
      case 'result':        renderResult(ctx);         break;
      default:              renderAvatarLoading();     break; // 'loading' phase
    }
  }

  // ── SIZE CARD ─────────────────────────────────────────────────────────────
  function renderSizeCard(
    sr: State['sizeRec'],
    category?: string,
  ): string {
    if (!sr || (!sr.upper && !sr.lower)) return '';

    // Determine which rows to show based on category
    const cat = (category ?? '').toLowerCase();
    const isTop    = /^(top|shirt|blouse|upper|tshirt|t-shirt|sweater|jacket|coat|hoodie)/.test(cat);
    const isBottom = /^(bottom|pants|jeans|trousers|skirt|shorts|lower|leggings|joggers)/.test(cat);
    const isDress  = /^(dress|jumpsuit|outfit|romper|overall)/.test(cat);

    // showUpper / showLower: explicit category → strict; no category → show whatever exists
    const showUpper = isBottom ? false : (isTop || isDress || !cat) ? true : true;
    const showLower = isTop   ? false : (isBottom || isDress || !cat) ? true : true;

    const upperRow = showUpper && sr.upper
      ? `<div class="ts-size-row"><span class="ts-size-label">Upper</span><span class="ts-size-value">${esc(sr.upper)}</span></div>`
      : '';
    const lowerRow = showLower && sr.lower
      ? `<div class="ts-size-row"><span class="ts-size-label">Lower</span><span class="ts-size-value">${esc(sr.lower)}</span></div>`
      : '';

    if (!upperRow && !lowerRow) return '';

    const RULER_SVG = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="${PDK}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="10" rx="2"/><line x1="6" y1="7" x2="6" y2="12"/><line x1="10" y1="7" x2="10" y2="11"/><line x1="14" y1="7" x2="14" y2="12"/><line x1="18" y1="7" x2="18" y2="11"/></svg>`;

    return `
    <div class="ts-size-card">
      <div class="ts-size-title">${RULER_SVG} YOUR RECOMMENDED SIZE</div>
      ${upperRow}${lowerRow}
    </div>`;
  }

  renderView();
  renderedOnce = true;
  updateLastState();

  // Shared-element open: expand the modal from the origin (trigger button) or,
  // when no origin is available (WooCommerce/Shopify), zoom in from the center.
  // Content starts hidden and fades in after the container settles.
  if (!prefersReducedMotion()) {
    hideContent(content);
    const reveal = (): void => { void fade(content, 1, FADE_MS); };
    if (originRect) {
      morphIn(card, originRect, MORPH_MS, reveal);
    } else {
      const r = card.getBoundingClientRect();
      if (r.width > 0) {
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const zoom = new DOMRect(
          cx - r.width * 0.46,
          cy - r.height * 0.46,
          r.width * 0.92,
          r.height * 0.92,
        );
        morphIn(card, zoom, MORPH_MS, reveal);
      } else {
        showContent(content);
      }
    }
  }

  return destroy;
}

// ── PUBLIC FACTORY ────────────────────────────────────────────────────────────

export function createTryOnButton(config: TryOnButtonConfig): void {
  injectStyles();

  const {
    container,
    label = 'Try This On',
    firebaseConfig,
    retailerId, productId, productName, productImage, category, onResult, showBranding = false, shimmer, colorScheme,
    ...sdkConfig
  } = config;

  const client = new TryStyleClient(sdkConfig);
  let resolvedRetailerId: string | null = retailerId ?? null;
  let resolvedFirebase: FirebaseConfig | undefined = firebaseConfig;
  let resolvedProductName: string | undefined = productName;
  let resolvedProductImage: string | undefined = productImage;
  let resolvedSubcategory: string | undefined;
  let resolvedTryonType: string | undefined;

  /**
   * Pick the most specific garment subcategory from a product's category list.
   * The backend returns categoryNames (e.g. ["Women", "Dresses", "Maxi Dresses"]);
   * we take the last entry as the most specific one, and strip any path-like
   * prefix ("Tops/Cardigan" -> "Cardigan") so the "Try this …" label shows the
   * actual product subcategory.
   */
  function pickSubcategory(p: any): string | undefined {
    const names = p?.categoryNames;
    if (Array.isArray(names)) {
      for (let i = names.length - 1; i >= 0; i--) {
        const n = names[i];
        if (typeof n === 'string' && n.trim()) {
          const leaf = n.trim().split(/[>/]/).pop()?.trim();
          return leaf || undefined;
        }
      }
    }
    if (p?.categories && Array.isArray(p.categories)) {
      const last = p.categories[p.categories.length - 1];
      if (last && typeof last === 'object' && last.name) {
        const leaf = String(last.name).trim().split(/[>/]/).pop()?.trim();
        return leaf || undefined;
      }
    }
    return undefined;
  }

  // Render wrapper
  const wrapper = el('div');
  wrapper.style.cssText = 'display:inline-flex;flex-direction:column;align-items:center';

  const btnWrap = el('div', 'ts-tryon-btn-wrap');
  const btn = el('button', 'ts-tryon-btn');
  btn.setAttribute('aria-haspopup', 'dialog');
  btn.innerHTML = `<span>${esc(label)}</span>`;

  // Shimmer sweep — class names drive the CSS animation in styles.ts.
  const shimmerFx = resolveShimmer(shimmer);
  if (shimmerFx.on) {
    btn.classList.add('ts-shimmer');
    if (shimmerFx.dir === 'right-to-left') btn.classList.add('ts-shimmer-rl');
    if (shimmerFx.diagonal) btn.classList.add('ts-shimmer-diag');
    btn.style.setProperty('--ts-shimmer-duration', shimmerFx.durationMs + 'ms');
  }

  const powered = showBranding ? el('div', 'ts-powered') : null;
  if (powered) powered.innerHTML = pwLogoHTML(18);

  btnWrap.appendChild(btn);
  wrapper.appendChild(btnWrap);
  if (powered) wrapper.appendChild(powered);
  container.appendChild(wrapper);

  // Init SDK — handle PKCE failure gracefully
  let ready = false;
  let initFailed = false;

  client.init().then(() => {
    ready = true;
    // Best-effort: load origin+product-scoped config (retailerId, firebase config, etc.)
    return client.platform.getSdkConfig<any>({
      productId,
      origin: typeof window !== 'undefined' ? window.location.origin : undefined,
    });
  }).then((cfg) => {
    if (cfg && typeof cfg === 'object') {
      if (!resolvedRetailerId && typeof cfg.retailerId === 'string' && cfg.retailerId) {
        resolvedRetailerId = cfg.retailerId;
      }
      if (!resolvedFirebase && cfg.firebaseConfig && typeof cfg.firebaseConfig === 'object') {
        const f = cfg.firebaseConfig as any;
        if (f.apiKey && f.authDomain && f.projectId) {
          resolvedFirebase = { apiKey: String(f.apiKey), authDomain: String(f.authDomain), projectId: String(f.projectId) };
        }
      }
    }

    // Best-effort: auto-resolve product title/image for UI (platform token only).
    // This endpoint is designed to match the backend's try-on product resolver.
    if (resolvedRetailerId && (resolvedProductName === undefined || resolvedProductImage === undefined)) {
      const pid = Array.isArray(productId) ? String(productId[0] || '') : String(productId || '');
      if (pid) {
        const apiUrl = client.apiUrl;
        const platformToken = client.platform.getCurrent();
        if (platformToken) {
          fetch(
            `${String(apiUrl).replace(/\/+$/, '')}/api/v1/wrapper/products/${encodeURIComponent(pid)}?retailerId=${encodeURIComponent(String(resolvedRetailerId))}`,
            { headers: { 'X-Platform-Token': platformToken } },
          )
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => {
              const p = j && j.product ? j.product : null;
              if (!p) return;
              if (resolvedProductName === undefined && p.title) resolvedProductName = String(p.title);
              if (resolvedProductImage === undefined) {
                const img = p.image || (p.images && p.images[0] && (typeof p.images[0] === 'string' ? p.images[0] : p.images[0].url));
                if (img) resolvedProductImage = String(img);
              }
              if (resolvedSubcategory === undefined) resolvedSubcategory = pickSubcategory(p);
              if (resolvedTryonType === undefined) {
                const cat = (p as any).category;
                resolvedTryonType =
                  tryonTypeFromTags(p?.tags) ??
                  ((typeof cat === 'string' && cat) ? toCanonicalCategory(cat) : null) ??
                  undefined;
              }
            })
            .catch(() => {});
        }
      }
    }

    // If retailerId is still missing after config fetch, client is not registered
    // or setup is incomplete — disable button with contact message.
    if (!resolvedRetailerId) {
      initFailed = true;
      btnWrap.classList.add('ts-disabled');
      btn.classList.add('ts-disabled');
      btn.innerHTML = esc(label);
      if (!wrapper.querySelector('.ts-init-error')) {
        const errMsg = el('div', 'ts-init-error');
        errMsg.textContent = INIT_ERR_MSG;
        wrapper.appendChild(errMsg);
      }
    }
  }).catch((e: Error) => {
    console.error('[TryStyle] init failed:', e.message);
    initFailed = true;

    // Disable button and show error message
    btnWrap.classList.add('ts-disabled');
    btn.classList.add('ts-disabled');
    btn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="11" stroke="rgba(255,255,255,.6)" stroke-width="1.5"/><path d="M8 12.5c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke="#fff" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="14.5" r="2" fill="#fff"/></svg>${esc(label)}`;

    if (!wrapper.querySelector('.ts-init-error')) {
      const errMsg = el('div', 'ts-init-error');
      errMsg.textContent = INIT_ERR_MSG;
      wrapper.appendChild(errMsg);
    }
  });

  btn.addEventListener('click', () => {
    if (initFailed) return; // paired with .ts-disabled pointer-events:none in CSS
    if (document.querySelector('.ts-overlay')) return;
    if (!ready) { console.warn('[TryStyle] SDK not ready yet.'); return; }
    const rid = resolvedRetailerId || retailerId;
    if (!rid) {
      console.error('[TryStyle] Missing retailerId. Configure this origin in the admin panel or pass retailerId explicitly.');
      return;
    }
    // Shared-element origin: the trigger button's geometry seeds the morph.
    const originRect = btn.getBoundingClientRect();
    const openModal = (origin?: DOMRect, initial?: Phase): void => {
      buildModal(
        {
          retailerId: rid,
          productId,
          productName: resolvedProductName,
          productImage: resolvedProductImage,
          firebaseConfig: resolvedFirebase,
          category,
          subcategory: resolvedSubcategory,
          tryonType: resolvedTryonType,
          onResult,
          showBranding,
          colorScheme,
        },
        client,
        origin ?? originRect,
        initial,
      );
    };

    // Resolve the garment subcategory in the background while the height dialog
    // is showing, so the modal's "Try this <subcategory>" label is ready.
    if (!resolvedSubcategory) {
      const pid = Array.isArray(productId) ? String(productId[0] || '') : String(productId || '');
      const apiUrl = client.apiUrl;
      const platformToken = client.platform.getCurrent();
      if (pid && platformToken) {
        fetch(
          `${String(apiUrl).replace(/\/+$/, '')}/api/v1/wrapper/products/${encodeURIComponent(pid)}?retailerId=${encodeURIComponent(rid)}`,
          { headers: { 'X-Platform-Token': platformToken } },
        )
          .then((r) => (r.ok ? r.json() : null))
          .then((j) => {
            const p = j && j.product ? j.product : null;
            if (!p) return;
            if (resolvedProductName === undefined && p.title) resolvedProductName = String(p.title);
            if (resolvedProductImage === undefined) {
              const img = p.image || (p.images && p.images[0] && (typeof p.images[0] === 'string' ? p.images[0] : p.images[0].url));
              if (img) resolvedProductImage = String(img);
            }
            if (resolvedSubcategory === undefined) resolvedSubcategory = pickSubcategory(p);
          })
          .catch(() => {});
      }
    }

    // Guests collect their height first (a phase of the same modal — it morphs
    // seamlessly into the upload flow). Logged-in users skip to their avatars.
    const isRealUser = client.auth.isAuthenticated() && !client.auth.getCurrentUser()?.isGuest;
    if (isRealUser) {
      openModal();
    } else {
      openModal(undefined, 'height');
    }
  });
}
